import React from 'react'
import { useTheme } from '@/context/ThemeContext'

type Props = {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

// Small themed on/off switch — no headless/radix dependency is installed for
// this, so it's a plain button with role="switch" (WAI-ARIA switch pattern).
export const Switch: React.FC<Props> = ({
  checked,
  onCheckedChange,
  disabled,
  id,
  ...aria
}) => {
  const { currentTheme: t } = useTheme()

  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={aria['aria-label']}
      aria-labelledby={aria['aria-labelledby']}
      aria-describedby={aria['aria-describedby']}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          if (!disabled) onCheckedChange(!checked)
        }
      }}
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      style={{
        backgroundColor: checked ? t.accent : t.border,
        // @ts-expect-error -- CSS custom property for the focus ring color/offset, not a typed style key.
        '--tw-ring-color': t.accent,
        '--tw-ring-offset-color': t.secondary,
      }}
    >
      <span
        className="inline-block h-4 w-4 transform rounded-full shadow transition-transform duration-200"
        style={{
          backgroundColor: t.buttonText,
          transform: checked ? 'translateX(22px)' : 'translateX(4px)',
        }}
      />
    </button>
  )
}

export default React.memo(Switch)
